<!-- @catalog The main status/label badge (design-system tag): label with optional icon or colored status dot (dot-color), tooltip, soft (inverse), solid or outline color variants, removable via the removable prop. For chips inside a multi-select use CInputChip. -->
<template>
  <q-badge v-bind="$attrs" :class="tagClass">
    <span
      v-if="dotColor"
      class="c-badge__dot"
      :style="{ backgroundColor: dotColor }"
    ></span>

    <q-icon v-if="icon" :name="icon" size="12px" />

    <span v-if="hasLabel">{{ label }}</span>

    <button
      v-if="removable"
      type="button"
      class="c-badge__remove"
      :aria-label="removeLabel"
      :disabled="disable"
      @mousedown.stop.prevent
      @click.stop.prevent="handleRemove"
    >
      <q-icon name="fa-solid fa-xmark" size="10px" />
    </button>

    <c-tooltip v-if="tooltip">{{ tooltip }}</c-tooltip>
  </q-badge>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    color?:
      | "primary"
      | "secondary"
      | "info"
      | "warning"
      | "negative"
      | "positive"
      | "blue"
      | "aqua"
      | "green"
      | "yellow"
      | "red"
      | "purple"
      | "grey"
      | "gray"
      | "portfolio-tag"
      | "portfolio-skill"
      | "portfolio-lob_competence"
      | "portfolio-concept";
    tooltip?: string | null;
    label?: string | number;
    inverse?: boolean;
    outline?: boolean;
    removable?: boolean;
    disable?: boolean;
    icon?: string;
    /**
     * Renders a small colored status dot before the label, for dynamic
     * (JS-driven) colors that have no tag color variant — e.g. chart legends.
     */
    dotColor?: string | null;
  }>(),
  {
    color: "primary",
    tooltip: null,
    label: undefined,
    inverse: true,
    outline: false,
    removable: false,
    disable: false,
    icon: undefined,
    dotColor: null,
  },
);

const emit = defineEmits<{
  (event: "remove"): void;
}>();

const handleRemove = () => {
  if (props.disable) {
    return;
  }

  emit("remove");
};

// label is string | number, so a numeric 0 is a valid label; only undefined
// and an empty string count as "no label".
const hasLabel = computed(
  () => props.label !== undefined && props.label !== "",
);

const removeLabel = computed(() =>
  hasLabel.value ? `${props.label} verwijderen` : "Verwijderen",
);

// Dynamically compute the class based on color
const tagClass = computed(() => {
  const classes = props.outline
    ? ["tag--outline"]
    : [
        ...(props.color ? [`tag--${props.color}`] : ["tag--primary"]),
        ...(props.inverse ? ["tag--inverse"] : []),
      ];

  if (props.disable) {
    classes.push("tw-opacity-60");
  }

  return classes;
});
</script>

<style lang="scss" scoped>
div.q-badge {
  @apply tw-h-[28px] tw-max-w-60 tw-select-none tw-items-center tw-gap-2.5 tw-overflow-hidden tw-px-2;

  > span {
    @apply tw-truncate tw-font-semibold tw-leading-[28px];
  }

  // Guarded, or this scoped rule would flatten Quasar's `rounded` pill.
  &:not(.q-badge--rounded) {
    @apply tw-rounded-lg;
  }

  .c-badge__dot {
    @apply tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full;
  }

  .c-badge__remove {
    @apply tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-inherit tw-opacity-60 tw-transition-opacity;

    &:hover {
      @apply tw-opacity-100;
    }

    &:disabled {
      @apply tw-cursor-default;
    }
  }

  .c-badge__remove {
    @apply tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-inherit tw-opacity-60 tw-transition-opacity;

    &:hover {
      @apply tw-opacity-100;
    }

    &:disabled {
      @apply tw-cursor-default;
    }
  }
}
</style>
